import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2, ShoppingBag, Truck } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import useAccessibleDialog from "@/hooks/useAccessibleDialog";
import { money } from "@/lib/money";

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal, discount, total, purchaseType, setPurchaseType } = useCart();
  const navigate = useNavigate();
  const dialog = useRef(null);
  useAccessibleDialog(dialog, isOpen, () => setIsOpen(false));

  useEffect(() => {
    if (isOpen) toast.dismiss();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const goCheckout = () => {
    setIsOpen(false);
    navigate("/checkout");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-dialog-backdrop aria-hidden="true" onClick={() => setIsOpen(false)} className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm" />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            ref={dialog} role="dialog" aria-modal="true" aria-labelledby="cart-title" tabIndex={-1} data-testid="cart-drawer"
            className="fixed right-0 top-0 z-[70] flex h-full w-full max-w-md flex-col bg-[#161616] border-l border-white/10"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
              <h2 id="cart-title" className="font-display text-xl font-extrabold uppercase tracking-tight text-white">Your Cart</h2>
              <button onClick={() => setIsOpen(false)} aria-label="Close cart" data-testid="cart-close-button"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-[#F7F6F0] hover:text-white">
                <X size={18} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/5">
                  <ShoppingBag size={26} className="text-[#A1A1A6]" />
                </div>
                <p className="text-[#A1A1A6]">Your cart is empty.</p>
                <button onClick={() => setIsOpen(false)} data-testid="cart-continue-shopping"
                  className="rounded-full bg-[#D6A921] px-6 py-3 text-sm font-bold text-[#080806]">Shop Protein</button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 border-b border-white/10 px-6 py-3 text-xs text-[#D6A921]">
                  <Truck size={14} /> Free UK delivery
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                  {items.map((it) => (
                    <div key={`${it.id}-${it.flavor}`} data-testid={`cart-item-${it.id}`}
                      className="flex gap-4 rounded-xl border border-white/10 bg-[#30302B] p-3">
                      <div className="h-20 w-16 shrink-0 overflow-hidden rounded-lg bg-black/40">
                        <img src={it.image_url} alt={`${it.name} pouch`} className="h-full w-full object-contain" />
                      </div>
                      <div className="flex flex-1 flex-col">
                        <div className="flex justify-between">
                          <div>
                            <p className="text-sm font-bold text-white leading-tight">{it.name}</p>
                            <p className="text-xs text-[#A1A1A6]">{it.flavor}</p>
                          </div>
                          <button onClick={() => removeItem(it.id, it.flavor)} aria-label={`Remove ${it.name} from cart`} data-testid={`cart-remove-${it.id}`} className="min-h-[24px] min-w-[24px] text-[#A1A1A6] hover:text-red-400">
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <div className="mt-auto flex items-center justify-between">
                          <div className="flex items-center gap-3 rounded-full border border-white/10 px-2 py-1">
                            <button onClick={() => updateQty(it.id, it.flavor, it.quantity - 1)} aria-label={`Decrease quantity of ${it.name}`} data-testid={`cart-dec-${it.id}`} className="min-h-[24px] min-w-[24px] text-[#F7F6F0] hover:text-white"><Minus size={14} /></button>
                            <span className="w-5 text-center text-sm font-semibold text-white">{it.quantity}</span>
                            <button onClick={() => updateQty(it.id, it.flavor, it.quantity + 1)} aria-label={`Increase quantity of ${it.name}`} data-testid={`cart-inc-${it.id}`} className="min-h-[24px] min-w-[24px] text-[#F7F6F0] hover:text-white"><Plus size={14} /></button>
                          </div>
                          <p className="text-sm font-bold text-white">{money(it.price * it.quantity)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-white/10 px-6 py-5 space-y-4">
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => setPurchaseType("one-time")} aria-pressed={purchaseType === "one-time"} data-testid="cart-onetime-toggle"
                      className={`rounded-lg border px-3 py-2.5 text-xs font-semibold transition-colors ${purchaseType === "one-time" ? "border-[#D6A921] bg-[#D6A921]/10 text-[#D6A921]" : "border-white/10 text-[#A1A1A6]"}`}>
                      One-time
                    </button>
                    <button onClick={() => setPurchaseType("subscribe")} aria-pressed={purchaseType === "subscribe"} data-testid="cart-subscribe-toggle"
                      className={`rounded-lg border px-3 py-2.5 text-xs font-semibold transition-colors ${purchaseType === "subscribe" ? "border-[#D6A921] bg-[#D6A921]/10 text-[#D6A921]" : "border-white/10 text-[#A1A1A6]"}`}>
                      Subscribe · Save 15%
                    </button>
                  </div>

                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between text-[#A1A1A6]"><span>Subtotal</span><span>{money(subtotal)}</span></div>
                    {discount > 0 && <div className="flex justify-between text-[#D6A921]"><span>Subscription (15%)</span><span>-{money(discount)}</span></div>}
                    <div className="flex justify-between text-[#A1A1A6]"><span>Delivery</span><span>FREE</span></div>
                    <div className="flex justify-between pt-2 text-base font-bold text-white"><span>Total</span><span data-testid="cart-total">{money(total)}</span></div>
                    <p className="pt-1 text-xs text-[#A1A1A6]">Yes, we’re still in D2C mode. You won’t find us anywhere else.</p>
                  </div>

                  <button onClick={goCheckout} data-testid="cart-checkout-button"
                    className="w-full rounded-full bg-[#D6A921] py-4 text-sm font-bold uppercase tracking-wide text-[#080806] hover:bg-white transition-colors">
                    Checkout
                  </button>
                  <button onClick={() => { setIsOpen(false); navigate("/cart"); }} className="w-full min-h-[44px] text-sm text-[#D6A921] underline">View cart</button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}



